import * as React from 'react';
import { MOBILE_VIDEO_STATUS } from '../../config/consts';
import { CALLS_STATUS, DEVICE_TYPE, LICENCE_STATUS, SERVICE, VERIFICATION_STATUS } from '../../config/devices';
import { isOpenRcConfigurationDisabled } from '../../containers/DeviceDetail/helpers';
import { useSiteContext } from '../../context/useSiteContext';
import { IDevice } from '../../modules/devices/store/deviceStore';
import { IMvLicenceModel } from '../../modules/sites/store';
import { useAppContext } from '../../pages/App/AppContextProvider';

interface IDeviceDataGridAction {
  activateTrialComponent: React.ReactElement;
  changePasswordComponent: React.ReactElement;
  device: IDevice;
  licenceModel: IMvLicenceModel;
  purchaseComponent: React.ReactElement;
  reverifyComponent: React.ReactElement;
  useAvailableCreditComponent: React.ReactElement;
}

const DeviceDataGridAction = ({
  activateTrialComponent,
  changePasswordComponent,
  device,
  licenceModel,
  purchaseComponent,
  reverifyComponent,
  useAvailableCreditComponent,
}: IDeviceDataGridAction) => {
  const isBillingTypePerDevice = useSiteContext().hasSiteBillingType('PER_DEVICE');
  const { feSiteRoles } = useAppContext();
  const canPurchase = feSiteRoles.isCompanyAdmin || feSiteRoles.isSiteAdminPayment;
  const shouldRenderReVerify =
    device.verificationStatus &&
    ![VERIFICATION_STATUS.VERIFIED, VERIFICATION_STATUS.ZOMBIE, VERIFICATION_STATUS.ARBITRARY_VERIFIED].includes(
      device?.verificationStatus
    );

  const shouldRenderChangePassword =
    device.hasDefaultPassword &&
    device.services?.[SERVICE.REMOTE_CONFIGURATION]?.active &&
    !isOpenRcConfigurationDisabled(
      device.services?.[SERVICE.REMOTE_CONFIGURATION]?.status,
      device.services?.[SERVICE.ACS]?.firmwareStatus
    );

  const shouldRenderActivateTrial =
    licenceModel?.status === MOBILE_VIDEO_STATUS.INITIAL &&
    [CALLS_STATUS.UNLICENSED, CALLS_STATUS.LOCAL].includes(device.callsStatus as CALLS_STATUS);

  const isDeviceUnlicensed = device.licenceStatus === LICENCE_STATUS.UNLICENSED;

  const reactivableModels = [
    MOBILE_VIDEO_STATUS.STANDARD,
    MOBILE_VIDEO_STATUS.TRIAL_EXPIRED,
    MOBILE_VIDEO_STATUS.STANDARD_EXPIRED,
  ];
  const isReactivable = reactivableModels.includes(licenceModel?.status) && isDeviceUnlicensed;
  const isElectronicLock = device.type === DEVICE_TYPE.DORMAKABA_ELECTRONIC_LOCK;
  const canUseAvailableCredit = [DEVICE_TYPE.HIPMO, DEVICE_TYPE.DORMAKABA_ELECTRONIC_LOCK].includes(device.type);
  // Electronic lock licences are always billed per device, the site mobile video billing type doesn't apply to them
  const isLicencedPerDevice = isBillingTypePerDevice || isElectronicLock;

  // A single lock is licenced from any non-empty pool, even when the pool doesn't cover every unlicensed device
  const hasCredits = licenceModel?.licencesPool > 0;
  const isCoveredByPool = isElectronicLock
    ? hasCredits
    : hasCredits && licenceModel?.additional <= licenceModel?.licencesPool;
  const needsMoreCredits = isElectronicLock ? !hasCredits : licenceModel?.additional > licenceModel?.licencesPool;

  if (shouldRenderReVerify) {
    return reverifyComponent;
  }

  if (shouldRenderChangePassword) {
    return changePasswordComponent;
  }

  if (isLicencedPerDevice && isReactivable) {
    if (canUseAvailableCredit && isCoveredByPool) {
      return useAvailableCreditComponent;
    }
    if (canPurchase && needsMoreCredits) {
      return purchaseComponent;
    }
  }

  if (isBillingTypePerDevice && shouldRenderActivateTrial) {
    return activateTrialComponent;
  }

  return null;
};

export default DeviceDataGridAction;
